import { formatDate as asdFormatDate } from "@a-safe-digital/format-date"
import { Controller } from "@hotwired/stimulus"

import { resolveLocale } from "@shared/lib/date-locale-map"

/**
 * Connects to data-controller="contacts--format-datetime"
 *
 * Formats timeline timestamps CLIENT-SIDE (browser timezone + locale) so the
 * Hotwire V2 Contact Timeline matches its React sibling byte-for-byte. Two modes:
 *
 *  - Header timestamp (has `date`/`time` targets): sets each target to the
 *    date-fns "P" (short date) and "p" (short time) — mirrors
 *    contact-timeline-entry.tsx's `format(occurredAt, "P")` / `format(., "p")`.
 *  - Inline change-diff value (no targets): sets the element's own text to the
 *    combined "Pp" for a datetime, or "P" for a date-only value — mirrors the
 *    change-diff's formatDateTime ("Pp") / formatApiDateDisplay ("P").
 *
 * Uses @a-safe-digital/format-date's `formatDate` (same formatter React uses),
 * resolving the date-fns locale from document.documentElement.lang. Deliberately
 * NOT importing @shared/lib/format-date (keeps react-i18next out of the bundle).
 * Server-rendered text is the no-JS fallback, overwritten once JS runs.
 *
 * Date-only values ("YYYY-MM-DD") are parsed from their parts into a LOCAL date
 * so a value like "2026-10-20" never shifts a day across timezones (the same
 * guard parseApiDate gives the React path).
 */
export default class extends Controller {
  static values = { iso: String }
  static targets = ["date", "time"]

  connect() {
    if (!this.hasIsoValue || !this.isoValue) return

    const lang = document.documentElement.lang || navigator?.language || "en-GB"
    try {
      const locale = resolveLocale(lang)

      if (this.hasDateTarget || this.hasTimeTarget) {
        if (this.hasDateTarget) {
          this.dateTarget.textContent = asdFormatDate(this.isoValue, {
            locale,
            format: "P",
          })
        }
        if (this.hasTimeTarget) {
          this.timeTarget.textContent = asdFormatDate(this.isoValue, {
            locale,
            format: "p",
          })
        }
        return
      }

      // Inline change-diff value.
      const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.isoValue)
      if (dateOnlyMatch) {
        const [, y, m, d] = dateOnlyMatch
        const localDate = new Date(Number(y), Number(m) - 1, Number(d))
        this.element.textContent = asdFormatDate(localDate, {
          locale,
          format: "P",
        })
      } else {
        this.element.textContent = asdFormatDate(this.isoValue, {
          locale,
          format: "Pp",
        })
      }
    } catch (error) {
      console.warn("contacts--format-datetime: could not format", error)
    }
  }
}
